import { type FormEventHandler, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher, useRouteLoaderData } from '@remix-run/react';
import { ImageUpload } from '@shared/dream-components';

import Button from '~/components/Button';
import Input from '~/components/Forms/Input';
import StatusInputs from '~/components/Forms/StatusInputs';
import useToast, { type ToastData } from '~/hooks/useToast';
import type { RootLoaderData } from '~/root';

interface Props {
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  onSuccess?: () => void;
}

const ManageAction = ({ firstName, lastName, avatarUrl, onSuccess }: Props) => {
  const fetcher = useFetcher<{
    toast: ToastData;
  }>();
  const fetcherData = fetcher.data;
  const isSubmitting = fetcher.state === 'submitting';
  const isSuccess = fetcherData?.toast?.status === 'success';
  const { t } = useTranslation();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  const avatarFileRef = useRef<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleAvatarSelect = (file: File) => {
    avatarFileRef.current = file;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(file));
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (isSuccess && onSuccess) {
      onSuccess();
    }
  }, [isSuccess, onSuccess]);

  useToast(fetcherData?.toast);

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    if (!avatarFileRef.current) return;

    // Intercept for multipart when avatar is selected
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    formData.append('avatar', avatarFileRef.current);

    fetcher.submit(formData, {
      method: 'PATCH',
      action: '/profile',
      encType: 'multipart/form-data',
    });
  };

  return (
    <fetcher.Form method="patch" action="/profile" onSubmit={handleSubmit} className="relative">
      <div className="space-y-6">
        <div className="space-y-3">
          <div className="flex justify-center">
            <ImageUpload
              currentImageUrl={previewUrl || avatarUrl}
              onFileSelect={handleAvatarSelect}
              shape="circle"
              size={80}
              isLoading={isSubmitting}
            />
          </div>
          <Input
            name="first_name"
            autoComplete="given-name"
            label={`${t('manage.personal_details_form.first_name')}`}
            placeholder={`${t('manage.personal_details_form.first_name')}`}
            defaultValue={firstName}
            textColor={site.primary_theme.text_on_background_color}
          />
          <Input
            name="last_name"
            autoComplete="family-name"
            label={`${t('manage.personal_details_form.last_name')}`}
            placeholder={`${t('manage.personal_details_form.last_name')}`}
            defaultValue={lastName}
            textColor={site.primary_theme.text_on_background_color}
          />
          <StatusInputs name="profile" errorMessage={t('errors.default')} successMessage={t('toast.success')} />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            style={{
              color: site.primary_theme.text_on_primary_color,
              backgroundColor: site.primary_theme.primary_color,
            }}
          >
            {isSubmitting ? `${t('shared.actions.submitting')}...` : t('shared.cta.submit')}
          </Button>
        </div>
      </div>
    </fetcher.Form>
  );
};

export default ManageAction;
